import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ExternalLink, Github } from 'lucide-react';

const Portfolio = () => {
    const projects = [
        {
            title: 'E-Commerce Platform',
            category: 'Web Development',
            image: 'https://images.unsplash.com/photo-1557821552-17105176677c?auto=format&fit=crop&q=80&w=800',
            description: 'A full-featured online shopping experience with real-time inventory.',
            tags: ['React', 'Node.js', 'MongoDB']
        },
        {
            title: 'Fitness Tracking App',
            category: 'Mobile App',
            image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&q=80&w=800',
            description: 'Mobile application for tracking workouts and nutritional intake.',
            tags: ['React Native', 'Firebase', 'Redux']
        },
        {
            title: 'AI Analytics Dashboard',
            category: 'Data Analysis',
            image: 'https://images.unsplash.com/photo-1551288049-bbda48658a7d?auto=format&fit=crop&q=80&w=800',
            description: 'Intelligent dashboard for visualizing complex business data sets.',
            tags: ['Vue.js', 'Python', 'D3.js']
        },
        {
            title: 'Social Connect',
            category: 'Web App',
            image: 'https://images.unsplash.com/photo-1522542550221-31fd19075a2f?auto=format&fit=crop&q=80&w=800',
            description: 'A networking platform focused on niche professional communities.',
            tags: ['Next.js', 'PostgreSQL', 'Tailwind']
        },
        {
            title: 'FinTech Wallet',
            category: 'Mobile App',
            image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=800',
            description: 'Secure digital wallet with multi-currency support and instant transfers.',
            tags: ['Flutter', 'Rust', 'Go']
        },
        {
            title: 'Smart Home Hub',
            category: 'IoT Solution',
            image: 'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&q=80&w=800',
            description: 'Integrated platform for managing smart home devices from a single app.',
            tags: ['React', 'MQTT', 'Node.js']
        }
    ];

    return (
        <div className="portfolio-container">
            <section className="portfolio-hero">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="section-header"
                    >
                        <h1 className="section-title">Our Portfolio</h1>
                        <p className="section-subtitle">A collection of digital products we've crafted with passion.</p>
                    </motion.div>

                    <div className="portfolio-grid">
                        {projects.map((project, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className="portfolio-card glass-morphism"
                            >
                                <div className="project-image-container">
                                    <img src={project.image} alt={project.title} />
                                    <div className="project-overlay">
                                        <div className="overlay-btns">
                                            <Link to="/contact" className="icon-btn-circle main-btn"><ExternalLink size={20} /></Link>
                                            <Link to="/contact" className="icon-btn-circle secondary-btn"><Github size={20} /></Link>
                                        </div>
                                    </div>
                                </div>
                                <div className="project-info-box">
                                    <div className="cat-badge">{project.category}</div>
                                    <h3>{project.title}</h3>
                                    <p>{project.description}</p>
                                    <div className="project-tags-list">
                                        {project.tags.map((tag, j) => (
                                            <span key={j} className="tag-item">{tag}</span>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <style>{`
        .portfolio-container { padding-top: 100px; }
        .portfolio-hero { padding: 80px 0; }
        .section-header { text-align: center; margin-bottom: 6rem; }
        .section-title { font-size: 3.5rem; font-weight: 800; margin-bottom: 1.5rem; }
        
        .portfolio-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); gap: 3rem; }
        .portfolio-card { border-radius: 2.5rem; overflow: hidden; background: var(--bg-card); border: 1px solid var(--border-glass); transition: var(--transition); }
        .portfolio-card:hover { transform: translateY(-15px); border-color: var(--primary); box-shadow: 0 30px 60px rgba(0,0,0,0.5); }
        
        .project-image-container { position: relative; height: 260px; overflow: hidden; }
        .project-image-container img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1); }
        .portfolio-card:hover .project-image-container img { transform: scale(1.1); }
        
        .project-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(5, 5, 5, 0.7); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; opacity: 0; transition: var(--transition); }
        .portfolio-card:hover .project-overlay { opacity: 1; }
        
        .overlay-btns { display: flex; gap: 1.5rem; transform: translateY(20px); transition: var(--transition); }
        .portfolio-card:hover .overlay-btns { transform: translateY(0); }
        
        .icon-btn-circle { width: 55px; height: 55px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: var(--transition); }
        .main-btn { background: var(--primary); color: #000; }
        .main-btn:hover { transform: rotate(90deg) scale(1.1); }
        .secondary-btn { background: rgba(255,255,255,0.1); color: #fff; border: 1px solid var(--border-glass); }
        .secondary-btn:hover { background: #fff; color: #000; }
        
        .project-info-box { padding: 2.5rem; }
        .cat-badge { display: inline-block; color: var(--primary); font-size: 0.8rem; font-weight: 800; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 1rem; }
        .project-info-box h3 { font-size: 1.8rem; font-weight: 800; margin-bottom: 1rem; color: #fff; }
        .project-info-box p { color: var(--text-muted); font-size: 1.05rem; line-height: 1.6; margin-bottom: 2rem; }
        
        .project-tags-list { display: flex; flex-wrap: wrap; gap: 0.6rem; }
        .tag-item { padding: 0.4rem 1.2rem; background: rgba(255,255,255,0.05); border-radius: 2rem; font-size: 0.8rem; color: #ddd; border: 1px solid var(--border-glass); font-weight: 600; }

        @media (max-width: 1024px) {
          .portfolio-grid { grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); }
        }

        @media (max-width: 768px) {
          .portfolio-hero { padding: 60px 0; }
          .section-title { font-size: 2.8rem; }
          .portfolio-grid { grid-template-columns: 1fr; gap: 2.5rem; }
          .project-overlay { opacity: 1; background: rgba(5,5,5,0.3); backdrop-filter: none; height: 60px; top: auto; bottom: 0; justify-content: flex-end; padding-right: 1.5rem; }
          .overlay-btns { transform: none; gap: 1rem; }
          .icon-btn-circle { width: 40px; height: 40px; }
          .project-image-container { height: 220px; }
          .project-info-box { padding: 2rem 1.5rem; }
        }
      `}</style>
        </div>
    );
};

export default Portfolio;
